import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputAdornment,
  TextField,
} from '@mui/material';
import { useCreateAnalyzerDataSettings } from 'features/analyzerDataSettings/api/createAnalyzerDataSettings';
import { useAnalyzers } from 'features/analyzers/api/getAnalyzers';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Analyzer } from 'types';
import { useCreateTimeWarning } from './api/createTimeWarning';
import { useUpdateTimeWarning } from './api/updateTimeWarning';

interface Props {
  analyzer: Analyzer | undefined;
}

type AnalyzerSettingsForm = {
  warn_threshold: number | string;
  critical_threshold: number | string;
  disconnect_after_inactivity: boolean | number;
  record_last_trace_in_live: boolean | number;
};

const defaultValues: AnalyzerSettingsForm = {
  warn_threshold: 30,
  critical_threshold: 60,
  disconnect_after_inactivity: false,
  record_last_trace_in_live: false,
};

const AnalyzerSettings = ({ analyzer }: Props) => {
  const { data: analyzers } = useAnalyzers({});
  const createAnalyzerDataSettingsMutation = useCreateAnalyzerDataSettings({
    analyzerId: analyzer?.id,
  });
  const createTimeWarningMutation = useCreateTimeWarning({
    analyzerId: analyzer?.id,
  });
  const updateTimeWarningMutation = useUpdateTimeWarning({
    analyzerId: analyzer?.id,
  });
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty, isSubmitted },
  } = useForm<AnalyzerSettingsForm>({
    defaultValues,
  });

  const { time_warning: timeWarning, data_settings: initialDataSettings } =
    analyzer || {};

  useEffect(() => {
    if (analyzer) {
      reset({ ...timeWarning, ...initialDataSettings });
    }
  }, [timeWarning, initialDataSettings, analyzer, reset]);

  const onSubmit = async () => {
    if (!analyzer) return;

    const formState = getValues();
    await createAnalyzerDataSettingsMutation.mutateAsync({
      data: {
        disconnect_after_inactivity: !!formState.disconnect_after_inactivity,
        record_last_trace_in_live: !!formState.record_last_trace_in_live,
      },
      analyzerId: analyzer.id,
    });

    const timeWarningWithAnalyzerId = {
      warn_threshold: Number(formState.warn_threshold),
      critical_threshold: Number(formState.critical_threshold),
      analyzer_id: analyzer.id,
    };

    if (timeWarning) {
      await updateTimeWarningMutation.mutateAsync({
        data: timeWarningWithAnalyzerId,
        analyzerId: analyzer.id,
      });
    } else {
      await createTimeWarningMutation.mutateAsync({
        data: timeWarningWithAnalyzerId,
      });
    }
  };

  const handleApplyToAll = async () => {
    if (!analyzers) return;
    const formState = getValues();

    for (let analyzer of analyzers) {
      await createAnalyzerDataSettingsMutation.mutateAsync({
        data: {
          disconnect_after_inactivity: !!formState.disconnect_after_inactivity,
          record_last_trace_in_live: !!formState.record_last_trace_in_live,
        },
        analyzerId: analyzer.id,
      });

      await createTimeWarningMutation.mutateAsync({
        data: {
          analyzer_id: analyzer.id,
          warn_threshold: Number(formState.warn_threshold),
          critical_threshold: Number(formState.critical_threshold),
        },
      });
    }
  };

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item>
        <FormControl>
          <FormLabel component='legend'>Data Settings</FormLabel>
          <FormGroup>
            <Controller
              name='disconnect_after_inactivity'
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!analyzer}
                      checked={!!value}
                      onChange={onChange}
                      name='disconnect_after_inactivity'
                    />
                  }
                  label='Disconnect after inactivity'
                />
              )}
            />
            <Controller
              name='record_last_trace_in_live'
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!analyzer}
                      checked={!!value}
                      onChange={onChange}
                      name='record_last_trace_in_live'
                    />
                  }
                  label='Record last trace in live mode (For Marker API usage)'
                />
              )}
            />
          </FormGroup>
        </FormControl>
      </Grid>
      <Grid item>
        <FormLabel component='legend'>Time Warning Settings</FormLabel>
      </Grid>
      <Grid item style={{ maxWidth: '15rem' }}>
        <Controller
          name='warn_threshold'
          control={control}
          rules={{ required: false }}
          render={({ field: { onChange, value } }) => (
            <TextField
              id='warn_threshold'
              name='warn_threshold'
              label='Time Warning Warn Threshold'
              value={value}
              onChange={onChange}
              disabled={!analyzer}
              type='number'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>seconds</InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid item style={{ maxWidth: '15rem' }}>
        <Controller
          name='critical_threshold'
          control={control}
          rules={{ required: false }}
          render={({ field: { onChange, value } }) => (
            <TextField
              id='critical_threshold'
              name='critical_threshold'
              label='Time Warning Critical Threshold'
              value={value}
              onChange={onChange}
              disabled={!analyzer}
              type='number'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>seconds</InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid item container style={{ paddingBottom: '1.5rem' }} spacing={1}>
        <Grid item>
          <Button
            color='primary'
            variant='contained'
            component='span'
            onClick={() => handleSubmit(onSubmit)()}
            fullWidth
            disabled={!isDirty || isSubmitted}
          >
            Apply
          </Button>
        </Grid>
        <Grid item>
          <Button
            color='primary'
            variant='contained'
            component='span'
            onClick={handleApplyToAll}
            fullWidth
            disabled={!isDirty}
          >
            Apply to All Analyzers
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AnalyzerSettings;
